import {Directive, ElementRef, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {TableComponent} from '@blocks/table/table.component';
import {BlockStore} from '@blocks/block/block.store';
import {fromEvent, of, Subject, Subscription, timer} from 'rxjs';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {TableService} from '@blocks/table/table.service';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {distinctUntilChanged, switchMap, takeUntil} from 'rxjs/operators';
import {CellSelection} from '@blocks/table/cell-selection';
import Quill from 'quill';
import {RichTextService} from '@blocks/rich-text/rich-text.service';
import {BlockType, RichText, TableCell} from '@paperlessio/sdk/api/models';

@Directive({
  selector: 'table[copyPaste]',
  exportAs: 'copyPaste'
})
export class TableCopyPasteDirective implements OnInit, OnDestroy {
  private subs = new Subscription();

  // The fromEvent methods in addCopyPasteHandler() didn't like being added to a Subscription, so we do cleanup with this Subject.
  private removeCopyPasteHandlers$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private tc: TableComponent,
    @Optional() private richTextService: RichTextService,
    @Optional() private tableService: TableService,
    @Optional() private blockMutationService: BlockMutationService,
    @Optional() private blockChangeDetectionService: BlockChangeDetectionService,
    @Optional() private blockStore: BlockStore,
    @Optional() @Inject(BLOCKS_CONFIG) private blocksConfig: BlocksConfig) {
  }

  ngOnInit() {
    this.subs.add(this.tc.blockSelected$.pipe(distinctUntilChanged()).subscribe(blockSelected => {
      if (blockSelected) {
        this.addCopyPasteHandler();
      } else {
        this.removeCopyPasteHandlers$.next();
      }
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.removeCopyPasteHandlers$.next();
  }

  copy(event: ClipboardEvent) {
    const selectedChildren = this.tc?.getSelectedChildren();
    if (!selectedChildren) {
      return;
    }

    // Create an HTML table to add to the clipboard. This is used to copy into excel & co.
    const selectedChildrenHtml = document.createElement('table');

    // The textual representation of the table to be able to paste the stuff into a text-only editor
    let selectedChildrenText = '';

    // Iterate through selectedChildren to add them into the clipboard
    selectedChildren.forEach((row, rowIndex) => {
      const rowElement = document.createElement('tr');

      // Iterate through the cells to add them to our c&p variables
      row.forEach((cell, colIndex) => {
        const cellElement = document.createElement('td');
        cellElement.innerHTML = cell.children[0].settings.content;
        rowElement.append(cellElement);

        selectedChildrenText += cellElement.innerText;
        if (colIndex < row.length) {
          selectedChildrenText += '\t';
        }
      });

      if (rowIndex < selectedChildren.length) {
        selectedChildrenText += '\n';
      }

      selectedChildrenHtml.append(rowElement);
    });

    const copyItems: {type: string, data: string}[] = [];

    // Add our custom data to the clipboard
    copyItems.push({
      type: `x-paperless/${this.tc.block.type}`,
      data: JSON.stringify(selectedChildren.map(row => row.map(cell => ({...cell}))))
    });

    // Add the html table to the clipboard
    copyItems.push({
      type: `text/html`,
      data: selectedChildrenHtml.outerHTML
    });

    // Add the pure text to the clipboard
    copyItems.push({
      type: `text/plain`,
      data: selectedChildrenText
    });

    copyItems.forEach(item => event.clipboardData.setData(item.type, item.data));

    document.dispatchEvent(new CustomEvent<any>('paperless.copy', {detail: copyItems}));

    event.preventDefault();
  }

  paste(event: ClipboardEvent) {
    // Get the clipboard text
    const clipboardPlainText = event.clipboardData.getData('text');
    const clipboardHtmlTable = event.clipboardData.getData('text/html');
    const clipboardPaperlessTable = event.clipboardData.getData(`x-paperless/${this.tc.block.type}`);

    if (!clipboardPlainText && !clipboardHtmlTable && !clipboardPaperlessTable) {
      return;
    }

    const quillElement = document.createElement('div');
    const quill = new Quill(quillElement);

    // Get our custom data from the clipboard
    let tablePasteData: any[][] = (clipboardPaperlessTable != null && clipboardPaperlessTable.length > 0) ? JSON.parse(clipboardPaperlessTable) : null;

    // Create a div to parse the html content from the clipboard
    const parseDiv = document.createElement('div');
    parseDiv.innerHTML = clipboardHtmlTable;

    // Select a table element from the div
    const pasteHtml = parseDiv.querySelector('table');

    // Parse the html table (from excel, google sheets, etc) to a usable object when we don't have custom data
    if ((tablePasteData == null || tablePasteData.length <= 0) && pasteHtml != null) {
      const rows = pasteHtml.querySelectorAll('tr');
      tablePasteData = [];

      rows.forEach((row, rowIndex) => {
        tablePasteData.push([]);

        row.querySelectorAll('td').forEach((cell, colIndex) => {
          const tableCell = new TableCell();
          tableCell.settings.backgroundColor = (cell.style.backgroundColor === 'transparent' || cell.style.backgroundColor === '#ffffff')
            ? null
            : cell.style.backgroundColor;

          // TODO: Check if td has children. If not, the text styles must lay in the td tag. Idea: just use the outerHTML and change the tagName to span
          const delta = quill.clipboard.convert(cell.innerHTML);
          quill.setContents(delta);

          (tableCell.children as any[]).push({
            type: BlockType.RichText,
            settings: {
              content: quill.getText(),
              delta: quill.getContents()
            }
          });

          tablePasteData[rowIndex].push(tableCell);
        });
      });
    }

    // When we got paste-able data, paste it here
    if (tablePasteData != null && this.tc.cellSelection && this.tc.cellSelection.width > 0 && this.tc.cellSelection.height > 0) {
      event.preventDefault();

      const pasteDataRows = tablePasteData.length;
      const pasteDataCols = tablePasteData?.[0].length;

      const tableRows = this.tc.getRowCount();
      const tableCols = this.tableService.getColumnCount();

      const selectionStartX = this.tc.cellSelection?.x1 ?? 0;
      const selectionStartY = this.tc.cellSelection?.y1 ?? 0;

      const widthToEnd = tableCols - selectionStartX;
      const heightToEnd = tableRows - selectionStartY;

      const newSelectionWidth = Math.max(pasteDataCols, this.tc.cellSelection.width);
      const newSelectionHeight = Math.max(pasteDataRows, this.tc.cellSelection.height);

      this.subs.add(timer(0)
        .pipe(
          switchMap(x => {
            // Selection is higher than remaining table space
            // Table rows need to be added
            if (heightToEnd < pasteDataRows) {
              this.blockMutationService.begin(this.tc.block.bucket_type, this.tc.block.bucket_id);

              for (let rows = 0; rows < (pasteDataRows - heightToEnd); rows++) {
                this.tableService.addRowAtIndex(null);
              }

              return this.blockMutationService.commit();
            } else {
              return of(x);
            }
          }),
          switchMap(x => {
            // Selection is wider than remaining table space
            // Table cols need to be added
            if (widthToEnd < pasteDataCols) {
              this.blockMutationService.begin(this.tc.block.bucket_type, this.tc.block.bucket_id);

              for (let cols = 0; cols < (pasteDataCols - widthToEnd); cols++) {
                this.tableService.addColAtIndex(this.tc.block, null);
              }

              return this.blockMutationService.commit();
            } else {
              return of(x);
            }
          }),
          switchMap(x => {
            return this.tc.fixColSizes();
          })
        )
        .subscribe({
          complete: () => {
            this.blockMutationService.begin(this.tc.block.bucket_type, this.tc.block.bucket_id);

            const cs = new CellSelection({
              x1: this.tc.cellSelection.x1,
              y1: this.tc.cellSelection.y1,
              x2: this.tc.cellSelection.x1 + newSelectionWidth - 1,
              y2: this.tc.cellSelection.y1 + newSelectionHeight - 1
            });

            const selectedChildren = cs.getSelectedChildren(this.tc.block);
            const selectedRTEs = selectedChildren.map(row => row.map(cell => cell.children[0]));
            selectedChildren.forEach((row, rowIndex) => {
              row.forEach((cell, colIndex) => {
                const newData = tablePasteData[rowIndex % tablePasteData.length][colIndex % tablePasteData[0].length];
                cell.settings.backgroundColor = newData.settings.backgroundColor;
                this.blockMutationService.addBlockUpdate(cell);
              });
            });

            try {
              selectedRTEs.forEach((row, rowIndex) => {
                row.forEach((rte, colIndex) => {
                  const newData = tablePasteData[rowIndex % tablePasteData.length][colIndex % tablePasteData[0].length].children[0] as RichText;
                  rte.styles = newData.styles;
                  rte.settings = newData.settings;
                  this.blockMutationService.addBlockUpdate(rte);
                });
              });
            } catch (e) {
            }

            this.blockMutationService.commit().subscribe(x => this.tc.cellSelection = cs);
          }
        }));
    }

    if (clipboardPlainText?.length > 0 && this.tc.blockSelected && this.tc.cellSelection?.isSingleCellSelection) {
      return;
    }
  }

  private addCopyPasteHandler() {
    fromEvent<ClipboardEvent>(document, 'copy').pipe(takeUntil(this.removeCopyPasteHandlers$)).subscribe(e => this.copy(e));
    fromEvent<ClipboardEvent>(document, 'paste').pipe(takeUntil(this.removeCopyPasteHandlers$)).subscribe(e => this.paste(e));
  }
}
